"use client";

import { ArrowRight, Play } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRef, useState } from "react";
import { GRID_CLASSES } from "@/libs/grid/grid";

export const aboutCards = [
  {
    type: "image",
    image: "/images/shop/home.jpg",
    alt: "Farmer using agricultural equipment",
  },
  {
    type: "content",
    number: "5+",
    title: "Trusted Brands",
    description:
      "We work with carefully selected manufacturers known for quality, performance, and long-term reliability.",
  },
  {
    type: "video",
    image: "/images/vida.mp4",
    alt: "Generator installation",
  },
  {
    type: "content",
    number: "1,500+",
    title: "Customers Served",
    description:
      "From farmers and contractors to businesses and institutions, we're proud to support customers across Kenya.",
  },
];

function VideoCard({ src, alt }: { src: string; alt: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  return (
    <button
      type="button"
      onClick={togglePlay}
      aria-label={isPlaying ? `Pause ${alt}` : `Play ${alt}`}
      className="group relative w-full h-full cursor-pointer"
    >
      <video
        ref={videoRef}
        src={src}
        playsInline
        onEnded={() => setIsPlaying(false)}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {!isPlaying && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors duration-300 group-hover:bg-black/30">
          <span className="flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white/90 group-hover:bg-white transition-colors duration-300">
            <Play className="w-6 h-6 lg:w-7 lg:h-7 text-black ml-0.5" fill="currentColor" />
          </span>
        </span>
      )}
    </button>
  );
}

export default function AboutUs() {
  return (
    <div className="flex flex-col w-full bg-black text-white py-10 lg:py-20 gap-6 lg:gap-16">
      <div className={`items-start relative w-full ${GRID_CLASSES}`}>
        <span className="col-span-6 lg:col-span-3 inline-flex items-center shrink-0 text-white/70 uppercase gap-2 pr-0 lg:pr-10 -translate-y-0.5 lg:-translate-y-1 text-base lg:text-xl 3xl:text-lg gap-1 lg:gap-3">
          <span className="shrink-0 w-3 h-3 lg:w-3 lg:h-3 rounded-full bg-white/70" />
          Our Approach
        </span>

        <div className="col-span-6 lg:col-span-9 lg:col-start-4 flex flex-col items-start mt-0 lg:mt-0 gap-4 lg:gap-8">
          <p className="text-lg lg:text-2xl lg:max-w-4xl">
            Every project starts with a different challenge, which is why we don&apos;t believe in
            one-size-fits-all recommendations. We take the time to understand your application,
            recommend equipment that fits the job, and continue supporting you long after the
            sale.
          </p>

          <Link
            href="/about-us"
            className="inline-flex shrink-0 items-center justify-center gap-2 bg-orange-200 text-black px-3 text-sm py-2 lg:px-4 lg:py-4 lg:gap-3"
          >
            Learn more
            <ArrowRight className="shrink-0 w-4 lg:w-4 h-4 lg:h-4" />
          </Link>
        </div>
      </div>

      <div className="flex flex-col gap-12 lg:gap-30 lg:rounded-t-xl mt-18 lg:mt-0 text-black">
        <div className={`items-stretch w-full ${GRID_CLASSES}`}>
          {aboutCards.map((item, i) => (
            <div
              key={i}
              className="col-start-2 col-span-5 lg:col-start-auto lg:col-span-3 flex items-stretch"
            >
              {item.type === "image" || item.type === "video" ? (
                // Image/video cards are hidden on mobile so only text cards show.
                // They reappear at the lg breakpoint alongside the content cards.
                <div className="hidden lg:flex items-stretch relative w-full h-56 lg:h-72 overflow-hidden rounded-lg">
                  {item.type === "video" ? (
                    <VideoCard src={item.image} alt={item.alt} />
                  ) : (
                    <Image src={item.image} alt={item.alt} fill className="object-cover" />
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-stretch justify-center w-full border-t lg:border-none border-white bg-none lg:bg-primary rounded-none lg:rounded-lg px-0 lg:px-4 py-2 lg:py-8">
                  <h4 className="text-white font-bold lg:font-semibold text-6xl lg:text-5xl">{item.number}</h4>
                  <h3 className="text-2xl lg:text-2xl font-semibold lg:font-medium text-white/40 lg:text-black mt-1 lg:mt-4 leading-normal lg:leading-7 text-right lg:text-left pl-0 lg:pl-0">
                    {item.title}
                  </h3>
                  <p className="hidden lg:block text-xl lg:text-base text-black leading-5 mt-2">
                    {item.description}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}